import React, { useState, useCallback, useRef, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, storage } from '../firebaseInit';
import { useAuth } from '../contexts/AuthContext';
import { useBranding, DEFAULT_BRANDING, BRANDING_FONTS } from '../contexts/BrandingContext';
import { useNotification } from '../contexts/NotificationContext';
import * as firestorePaths from '../utils/firestorePaths';
import { isWhiteLabelEnabled, SUBSCRIPTION_STATUSES } from '../utils/usageTracking';
import Card from './shared/Card';
import Button from './shared/Button';
import FormField from './shared/FormField';
import LoadingSpinner from './shared/LoadingSpinner';
import ConfirmDialog from './shared/ConfirmDialog';

/**
 * TeamBrandingSettings Component
 * 
 * Allows team leads and admins to customize their team's branding:
 * - Company name
 * - Logo upload
 * - Primary and accent colors
 * - "Powered by" visibility toggle
 * 
 * Changes are reflected in real-time for all team members.
 */
const TeamBrandingSettings = ({ onClose }) => {
  // userTeam now auto-switches when admin views another team
  const { userTeam, claims, userEmail } = useAuth();
  const { branding, canEditBranding, DEFAULT_BRANDING: defaultBranding } = useBranding();
  const { showSuccess, showError, showWarning } = useNotification();
  
  // Form state
  const [companyName, setCompanyName] = useState(branding.companyName);
  const [tagline, setTagline] = useState(branding.tagline || '');
  const [showTaglineInSidebar, setShowTaglineInSidebar] = useState(branding.showTaglineInSidebar ?? true);
  const [showTaglineInBrowserTitle, setShowTaglineInBrowserTitle] = useState(branding.showTaglineInBrowserTitle ?? true);
  const [logoUrl, setLogoUrl] = useState(branding.logoUrl);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(branding.logoUrl);
  const [teamSlug, setTeamSlug] = useState(branding.teamSlug || '');
  const [slugError, setSlugError] = useState('');
  const [isEditingSlug, setIsEditingSlug] = useState(false);
  const [originalSlug, setOriginalSlug] = useState(branding.teamSlug || '');
  
  // Font and color state
  const [titleFont, setTitleFont] = useState(branding.titleFont || DEFAULT_BRANDING.titleFont);
  const [titleColor, setTitleColor] = useState(branding.titleColor || DEFAULT_BRANDING.titleColor);
  const [taglineFont, setTaglineFont] = useState(branding.taglineFont || DEFAULT_BRANDING.taglineFont);
  const [taglineColor, setTaglineColor] = useState(branding.taglineColor || DEFAULT_BRANDING.taglineColor);
  
  // Check if URL can be changed (once per month)
  const canChangeUrl = useCallback(() => {
    if (!branding.teamSlugLastChanged) return true; // Never changed before
    const lastChanged = branding.teamSlugLastChanged?.toDate ? 
      branding.teamSlugLastChanged.toDate() : 
      new Date(branding.teamSlugLastChanged);
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    return lastChanged < oneMonthAgo;
  }, [branding.teamSlugLastChanged]);
  
  const getDaysUntilUrlChange = useCallback(() => {
    if (!branding.teamSlugLastChanged) return 0;
    const lastChanged = branding.teamSlugLastChanged?.toDate ? 
      branding.teamSlugLastChanged.toDate() : 
      new Date(branding.teamSlugLastChanged);
    const nextAllowed = new Date(lastChanged);
    nextAllowed.setMonth(nextAllowed.getMonth() + 1);
    const now = new Date();
    const diffTime = nextAllowed - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }, [branding.teamSlugLastChanged]);
  
  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isDeletingLogo, setIsDeletingLogo] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  
  // White label entitlement state
  const [whiteLabelEntitled, setWhiteLabelEntitled] = useState(null); // null = loading
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  
  const fileInputRef = useRef(null);
  const appId = firestorePaths.getArtifactProjectId();
  
  // Load subscription data to check white label entitlement
  useEffect(() => {
    async function checkWhiteLabelEntitlement() {
      if (!userTeam) {
        setWhiteLabelEntitled(false);
        return;
      }
      
      try {
        const teamPath = firestorePaths.getTeamPath(appId, userTeam);
        const teamDoc = await getDoc(doc(db, teamPath));
        
        if (teamDoc.exists()) {
          const teamData = teamDoc.data();
          const subscription = teamData.subscription || {};
          
          setSubscriptionPlan(subscription.plan);
          
          // Check if white labeling is enabled
          const entitled = isWhiteLabelEnabled(subscription);
          setWhiteLabelEntitled(entitled);
          
          console.log('🎨 White label entitlement:', { 
            plan: subscription.plan, 
            status: subscription.status,
            entitled 
          });
        } else {
          setWhiteLabelEntitled(false);
        }
      } catch (error) {
        console.error('Error checking white label entitlement:', error);
        // Default to allowing if there's an error (backwards compatibility)
        setWhiteLabelEntitled(true);
      }
    }
    
    checkWhiteLabelEntitlement();
  }, [userTeam, appId]);

  // Track changes - moved before any conditional returns to follow hooks rules
  const checkForChanges = useCallback(() => {
    const changed = 
      companyName !== branding.companyName ||
      tagline !== (branding.tagline || '') ||
      showTaglineInSidebar !== (branding.showTaglineInSidebar ?? true) ||
      showTaglineInBrowserTitle !== (branding.showTaglineInBrowserTitle ?? true) ||
      teamSlug !== (branding.teamSlug || '') ||
      logoFile !== null ||
      titleFont !== (branding.titleFont || DEFAULT_BRANDING.titleFont) ||
      titleColor !== (branding.titleColor || DEFAULT_BRANDING.titleColor) ||
      taglineFont !== (branding.taglineFont || DEFAULT_BRANDING.taglineFont) ||
      taglineColor !== (branding.taglineColor || DEFAULT_BRANDING.taglineColor);
    setHasChanges(changed);
  }, [companyName, tagline, showTaglineInSidebar, showTaglineInBrowserTitle, teamSlug, logoFile, branding, titleFont, titleColor, taglineFont, taglineColor]);

  // Sync form state when branding updates from Firestore (real-time listener)
  useEffect(() => {
    setCompanyName(branding.companyName);
    setTagline(branding.tagline || '');
    setShowTaglineInSidebar(branding.showTaglineInSidebar ?? true);
    setShowTaglineInBrowserTitle(branding.showTaglineInBrowserTitle ?? true);
    setLogoUrl(branding.logoUrl);
    setLogoPreview(branding.logoUrl);
    setTeamSlug(branding.teamSlug || '');
    setOriginalSlug(branding.teamSlug || '');
    setIsEditingSlug(false);
    setTitleFont(branding.titleFont || DEFAULT_BRANDING.titleFont);
    setTitleColor(branding.titleColor || DEFAULT_BRANDING.titleColor);
    setTaglineFont(branding.taglineFont || DEFAULT_BRANDING.taglineFont);
    setTaglineColor(branding.taglineColor || DEFAULT_BRANDING.taglineColor);
    setHasChanges(false);
  }, [branding]);
  
  // Validate team slug
  const validateSlug = useCallback((slug) => {
    if (!slug) {
      setSlugError('');
      return true; // Empty is valid (optional)
    }
    
    // Must be lowercase alphanumeric with hyphens
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (!slugRegex.test(slug)) {
      setSlugError('Use only lowercase letters, numbers, and hyphens');
      return false;
    }
    
    // Minimum length
    if (slug.length < 3) {
      setSlugError('Must be at least 3 characters');
      return false;
    }
    
    // Maximum length
    if (slug.length > 50) {
      setSlugError('Must be 50 characters or less');
      return false;
    }
    
    setSlugError('');
    return true;
  }, []);
  
  // Auto-generate slug from company name
  const generateSlugFromName = useCallback((name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
      .replace(/\s+/g, '-')          // Spaces to hyphens
      .replace(/-+/g, '-')           // Multiple hyphens to single
      .replace(/^-|-$/g, '')         // Trim hyphens from ends
      .substring(0, 50);             // Limit length
  }, []);

  // Handle logo file selection
  const handleLogoSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      showError('Please select a valid image file (JPEG, PNG, GIF, WebP, or SVG)');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      showError('Logo file must be smaller than 2MB');
      return;
    }

    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    setHasChanges(true);
  }, [showError]);
  
  // Check if user has role permission
  if (!canEditBranding()) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h3 className="text-lg font-medium text-yellow-800 mb-2">Access Restricted</h3>
        <p className="text-yellow-700">
          Only Team Leads and Admins can customize team branding.
          Contact your team lead if you need branding changes.
        </p>
      </div>
    );
  }
  
  // Show loading while checking entitlement
  if (whiteLabelEntitled === null) {
    return (
      <div className="p-4 flex justify-center">
        <LoadingSpinner size="medium" message="Loading branding settings..." />
      </div>
    );
  }
  
  // Check if white labeling is enabled for the subscription
  if (!whiteLabelEntitled) {
    return (
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <span className="text-2xl">🎨</span>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-blue-800 mb-2">White Labeling Not Included</h3>
            <p className="text-blue-700 mb-4">
              Custom branding (logo, company name, colors) is available with the <strong>Business</strong> plan 
              or as an add-on to your current plan.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-blue-600">
                <span>✓</span>
                <span>Custom company name and logo</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-blue-600">
                <span>✓</span>
                <span>Custom branded login page for your team</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-blue-600">
                <span>✓</span>
                <span>Remove &quot;Powered by Treegenie.ai&quot; branding</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-blue-200">
              <p className="text-sm text-blue-700 mb-3">
                Current plan: <strong>{subscriptionPlan ? subscriptionPlan.charAt(0).toUpperCase() + subscriptionPlan.slice(1).replace('_', ' ') : 'Unknown'}</strong>
              </p>
              <Button
                variant="primary"
                size="md"
                onClick={() => {
                  showWarning('Go to Settings → Subscription to upgrade your plan or add white labeling.');
                }}
              >
                Upgrade to Get White Labeling
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Upload logo to Firebase Storage
  const uploadLogo = async () => {
    if (!logoFile || !userTeam) return null;

    setIsUploadingLogo(true);
    try {
      // Create storage reference with team ID and timestamp
      const timestamp = Date.now();
      const fileExtension = logoFile.name.split('.').pop();
      const storagePath = `teams/${userTeam}/branding/logo_${timestamp}.${fileExtension}`;
      const storageRef = ref(storage, storagePath);

      // Upload file
      await uploadBytes(storageRef, logoFile);
      
      // Get download URL
      const downloadUrl = await getDownloadURL(storageRef);
      
      return downloadUrl;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Delete old logo from storage
  const deleteOldLogo = async (oldLogoUrl) => {
    if (!oldLogoUrl) return;
    
    try {
      // Extract storage path from URL
      // Firebase Storage URLs contain the path after '/o/' and before '?'
      const urlParts = oldLogoUrl.split('/o/');
      if (urlParts.length > 1) {
        const pathWithParams = urlParts[1];
        const path = decodeURIComponent(pathWithParams.split('?')[0]);
        const oldLogoRef = ref(storage, path);
        await deleteObject(oldLogoRef);
      }
    } catch (error) {
      // Don't fail if old logo deletion fails
      console.warn('Could not delete old logo:', error);
    }
  };

  // Handle logo removal
  const handleRemoveLogo = async () => {
    if (!logoUrl && !logoPreview) return;

    setIsDeletingLogo(true);
    try {
      // Delete from storage if it exists
      if (branding.logoUrl) {
        await deleteOldLogo(branding.logoUrl);
      }

      // Update Firestore
      const teamPath = firestorePaths.getTeamPath(appId, userTeam);
      await updateDoc(doc(db, teamPath), {
        'branding.logoUrl': null,
        'branding.updatedAt': new Date(),
        'branding.updatedBy': userEmail
      });

      setLogoUrl(null);
      setLogoPreview(null);
      setLogoFile(null);
      
      showSuccess('Logo removed successfully');
    } catch (error) {
      console.error('Error removing logo:', error);
      showError('Failed to remove logo. Please try again.');
    } finally {
      setIsDeletingLogo(false);
    }
  };

  // Check if slug is unique across all teams (via Cloud Function to avoid permission issues)
  const checkSlugUnique = async (slug) => {
    if (!slug) return true;
    
    try {
      const functions = getFunctions();
      const checkFn = httpsCallable(functions, 'checkSlugAvailability');
      const result = await checkFn({ slug });
      return result.data.available;
    } catch (error) {
      console.error('Error checking slug uniqueness:', error);
      // On error, allow save — server-side rules are the real enforcement
      return true;
    }
  };

  // Save branding changes
  const handleSave = async () => {
    if (!userTeam) {
      showError('No team selected. Please contact support.');
      return;
    }

    // Validate company name
    if (!companyName.trim()) {
      showWarning('Please enter a company name');
      return;
    }
    
    // Validate slug if provided
    if (teamSlug && !validateSlug(teamSlug)) {
      showWarning('Please fix the team URL before saving');
      return;
    }
    
    // Check slug uniqueness if changed
    if (teamSlug && teamSlug !== branding.teamSlug) {
      const isUnique = await checkSlugUnique(teamSlug);
      if (!isUnique) {
        setSlugError('This URL is already taken by another team');
        showWarning('This team URL is already taken. Please choose another.');
        return;
      }
    }

    setIsSaving(true);
    try {
      let newLogoUrl = logoUrl;

      // Upload new logo if selected
      if (logoFile) {
        // Delete old logo first
        if (branding.logoUrl) {
          await deleteOldLogo(branding.logoUrl);
        }
        newLogoUrl = await uploadLogo();
      }

      // Update team document with branding
      const teamPath = firestorePaths.getTeamPath(appId, userTeam);
      const updateData = {
        'branding.companyName': companyName.trim(),
        'branding.tagline': tagline.trim() || null,
        'branding.showTaglineInSidebar': showTaglineInSidebar,
        'branding.showTaglineInBrowserTitle': showTaglineInBrowserTitle,
        'branding.logoUrl': newLogoUrl,
        'branding.teamSlug': teamSlug || null,
        'branding.titleFont': titleFont,
        'branding.titleColor': titleColor,
        'branding.taglineFont': taglineFont,
        'branding.taglineColor': taglineColor,
        'branding.updatedAt': new Date(),
        'branding.updatedBy': userEmail
      };
      
      // Track when URL was changed (for once-per-month restriction)
      if (teamSlug !== originalSlug) {
        updateData['branding.teamSlugLastChanged'] = new Date();
      }
      
      await updateDoc(doc(db, teamPath), updateData);

      // Also update public branding collection for branded login pages
      // This collection is publicly readable for unauthenticated users
      if (teamSlug) {
        const { setDoc, deleteDoc } = await import('firebase/firestore');
        const publicBrandingPath = `artifacts/${appId}/publicTeamBranding/${teamSlug}`;
        console.log('📝 Saving public branding to:', publicBrandingPath);
        console.log('📝 Firestore app name:', db.app.name);
        console.log('📝 Firestore project:', db.app.options.projectId);
        try {
          await setDoc(doc(db, publicBrandingPath), {
            teamId: userTeam,
            companyName: companyName.trim(),
            tagline: tagline.trim() || null,
            logoUrl: newLogoUrl,
            titleFont: titleFont,
            titleColor: titleColor,
            taglineFont: taglineFont,
            taglineColor: taglineColor,
            updatedAt: new Date()
          });
          console.log('✅ Public branding saved successfully');
          
          // If slug changed, delete old public branding entry
          if (branding.teamSlug && branding.teamSlug !== teamSlug) {
            const oldPublicBrandingPath = `artifacts/${appId}/publicTeamBranding/${branding.teamSlug}`;
            await deleteDoc(doc(db, oldPublicBrandingPath));
          }
        } catch (publicBrandingError) {
          console.error('❌ Failed to save public branding:', publicBrandingError);
          // Don't throw - the main branding save succeeded
        }
      } else if (branding.teamSlug) {
        // Slug was removed, delete public branding entry
        const { deleteDoc } = await import('firebase/firestore');
        const oldPublicBrandingPath = `artifacts/${appId}/publicTeamBranding/${branding.teamSlug}`;
        await deleteDoc(doc(db, oldPublicBrandingPath));
      }

      setLogoFile(null);
      setHasChanges(false);
      
      showSuccess('Branding updated! Changes will appear for all team members.');
    } catch (error) {
      console.error('Error saving branding:', error);
      showError('Failed to save branding. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to defaults (with confirmation)
  const handleResetToDefaults = async () => {
    if (!userTeam) return;

    setIsSaving(true);
    try {
      // Delete logo if exists
      if (branding.logoUrl) {
        await deleteOldLogo(branding.logoUrl);
      }

      // Reset branding to defaults
      const teamPath = firestorePaths.getTeamPath(appId, userTeam);
      await updateDoc(doc(db, teamPath), {
        'branding.companyName': DEFAULT_BRANDING.companyName,
        'branding.tagline': DEFAULT_BRANDING.tagline,
        'branding.showTaglineInSidebar': DEFAULT_BRANDING.showTaglineInSidebar,
        'branding.showTaglineInBrowserTitle': DEFAULT_BRANDING.showTaglineInBrowserTitle,
        'branding.logoUrl': null,
        'branding.teamSlug': null,
        'branding.titleFont': DEFAULT_BRANDING.titleFont,
        'branding.titleColor': DEFAULT_BRANDING.titleColor,
        'branding.taglineFont': DEFAULT_BRANDING.taglineFont,
        'branding.taglineColor': DEFAULT_BRANDING.taglineColor,
        'branding.updatedAt': new Date(),
        'branding.updatedBy': userEmail
      });

      // Reset local state
      setCompanyName(DEFAULT_BRANDING.companyName);
      setTagline(DEFAULT_BRANDING.tagline);
      setShowTaglineInSidebar(DEFAULT_BRANDING.showTaglineInSidebar);
      setShowTaglineInBrowserTitle(DEFAULT_BRANDING.showTaglineInBrowserTitle);
      setTeamSlug('');
      setSlugError('');
      setLogoUrl(null);
      setLogoPreview(null);
      setLogoFile(null);
      setTitleFont(DEFAULT_BRANDING.titleFont);
      setTitleColor(DEFAULT_BRANDING.titleColor);
      setTaglineFont(DEFAULT_BRANDING.taglineFont);
      setTaglineColor(DEFAULT_BRANDING.taglineColor);
      setHasChanges(false);

      showSuccess('Branding reset to defaults');
    } catch (error) {
      console.error('Error resetting branding:', error);
      showError('Failed to reset branding. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Team Branding</h2>
          <p className="text-sm text-gray-600 mt-1">
            Customize how your team&apos;s app looks. Changes apply to all team members.
          </p>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Settings Form */}
        <Card padding="md">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Brand Settings</h3>
          
          <div className="space-y-4">
            {/* Company Name */}
            <FormField
              label="Company Name"
              name="companyName"
              value={companyName}
              onChange={(e) => {
                setCompanyName(e.target.value);
                setHasChanges(true);
              }}
              placeholder="Enter your company name"
              helpText="This appears in the app header and browser tab"
              required
            />

            {/* Tagline */}
            <FormField
              label="Tagline"
              name="tagline"
              value={tagline}
              onChange={(e) => {
                setTagline(e.target.value);
                setHasChanges(true);
              }}
              placeholder="e.g., The Call Script Genie"
              helpText="A catchphrase or subtitle for your company"
            />

            {/* Tagline Display Options */}
            {tagline && (
              <div className="pl-4 border-l-2 border-gray-200 space-y-2">
                <p className="text-xs text-gray-500 font-medium mb-2">Show tagline in:</p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showTaglineInSidebar}
                    onChange={(e) => {
                      setShowTaglineInSidebar(e.target.checked);
                      setHasChanges(true);
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Sidebar (below company name)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showTaglineInBrowserTitle}
                    onChange={(e) => {
                      setShowTaglineInBrowserTitle(e.target.checked);
                      setHasChanges(true);
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Browser tab title</span>
                </label>
              </div>
            )}

            {/* Title Font & Color */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Title Styling</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Font</label>
                  <select
                    value={titleFont}
                    onChange={(e) => {
                      setTitleFont(e.target.value);
                      setHasChanges(true);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {BRANDING_FONTS.map(font => (
                      <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                        {font.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={titleColor}
                      onChange={(e) => {
                        setTitleColor(e.target.value);
                        setHasChanges(true);
                      }}
                      className="w-16 h-10 rounded border border-gray-300 cursor-pointer p-1"
                    />
                    <input
                      type="text"
                      value={titleColor}
                      onChange={(e) => {
                        setTitleColor(e.target.value);
                        setHasChanges(true);
                      }}
                      className="w-20 px-2 py-2 border border-gray-300 rounded-md text-xs font-mono"
                      placeholder="#1D4ED8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Tagline Font & Color */}
            {tagline && (
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Tagline Styling</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Font</label>
                    <select
                      value={taglineFont}
                      onChange={(e) => {
                        setTaglineFont(e.target.value);
                        setHasChanges(true);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {BRANDING_FONTS.map(font => (
                        <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                          {font.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={taglineColor}
                        onChange={(e) => {
                          setTaglineColor(e.target.value);
                          setHasChanges(true);
                        }}
                        className="w-16 h-10 rounded border border-gray-300 cursor-pointer p-1"
                      />
                      <input
                        type="text"
                        value={taglineColor}
                        onChange={(e) => {
                          setTaglineColor(e.target.value);
                          setHasChanges(true);
                        }}
                        className="w-20 px-2 py-2 border border-gray-300 rounded-md text-xs font-mono"
                        placeholder="#6B7280"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Team URL Slug */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Team URL <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              
              {/* Show current URL if set and not editing */}
              {originalSlug && !isEditingSlug ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <span className="text-sm text-gray-600">
                      {window.location.origin}/team/<span className="font-medium text-gray-900">{originalSlug}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        if (canChangeUrl()) {
                          setIsEditingSlug(true);
                        }
                      }}
                      disabled={!canChangeUrl()}
                      className={`ml-auto text-xs px-2 py-1 rounded ${
                        canChangeUrl() 
                          ? 'text-blue-600 hover:bg-blue-50' 
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {canChangeUrl() ? '✏️ Edit URL' : '🔒 Locked'}
                    </button>
                  </div>
                  {!canChangeUrl() && (
                    <p className="text-xs text-amber-600">
                      ⏳ URL can be changed again in {getDaysUntilUrlChange()} days (once per month)
                    </p>
                  )}
                </div>
              ) : (
                <>
                  {/* Editing mode or no URL set */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 whitespace-nowrap">
                      {window.location.origin}/team/
                    </span>
                    <input
                      type="text"
                      value={teamSlug}
                      onChange={(e) => {
                        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                        setTeamSlug(value);
                        validateSlug(value);
                        setHasChanges(true);
                      }}
                      placeholder="your-company"
                      className={`flex-1 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        slugError ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {isEditingSlug && (
                      <button
                        type="button"
                        onClick={() => {
                          setTeamSlug(originalSlug);
                          setIsEditingSlug(false);
                          setSlugError('');
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                  {slugError ? (
                    <p className="text-xs text-red-500 mt-1">{slugError}</p>
                  ) : teamSlug ? (
                    <p className="text-xs text-green-600 mt-1">
                      ✓ Your team login URL: {window.location.origin}/team/{teamSlug}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">
                      Create a custom login URL for your team members
                    </p>
                  )}
                  {!teamSlug && companyName && companyName !== 'TreeGenie' && (
                    <button
                      type="button"
                      onClick={() => {
                        const suggested = generateSlugFromName(companyName);
                        setTeamSlug(suggested);
                        validateSlug(suggested);
                        setHasChanges(true);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-700 mt-1"
                    >
                      Suggest: {generateSlugFromName(companyName)}
                    </button>
                  )}
                  {isEditingSlug && (
                    <p className="text-xs text-amber-600 mt-2">
                      ⚠️ You can only change your team URL once per month
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Logo Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Logo
              </label>
              
              <div className="flex items-center gap-4">
                {/* Logo Preview */}
                <div 
                  className="w-16 h-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden"
                  style={{ minWidth: '64px' }}
                >
                  {(logoPreview || logoUrl) ? (
                    <img 
                      src={logoPreview || logoUrl} 
                      alt="Logo preview" 
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <span className="text-2xl">🏢</span>
                  )}
                </div>

                {/* Upload/Remove Buttons */}
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                    onChange={handleLogoSelect}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingLogo}
                  >
                    {isUploadingLogo ? 'Uploading...' : (logoPreview || logoUrl) ? 'Change Logo' : 'Upload Logo'}
                  </Button>
                  {(logoPreview || logoUrl) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveLogo}
                      disabled={isDeletingLogo}
                      className="text-red-600"
                    >
                      {isDeletingLogo ? 'Removing...' : 'Remove Logo'}
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Max 2MB. Recommended: Square image, at least 128x128px
              </p>
            </div>

          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-200">
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
            >
              {isSaving ? <LoadingSpinner size="small" /> : 'Save Changes'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowResetConfirm(true)}
              disabled={isSaving}
            >
              Reset to Defaults
            </Button>
          </div>
        </Card>

        {/* Live Preview */}
        <Card padding="md">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Live Preview</h3>
          
          {/* Preview - Sidebar Header */}
          <div 
            className="rounded-lg overflow-hidden border border-gray-200"
            style={{ backgroundColor: '#f9fafb' }}
          >
            {/* Simulated Sidebar Header */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center gap-2">
                {(logoPreview || logoUrl) ? (
                  <img 
                    src={logoPreview || logoUrl} 
                    alt="Logo" 
                    className="w-8 h-8 object-contain rounded"
                  />
                ) : (
                  <span className="text-xl">✨</span>
                )}
                <div>
                  <h4 
                    className="font-bold leading-tight"
                    style={{ 
                      fontFamily: titleFont,
                      color: titleColor 
                    }}
                  >
                    {companyName || 'Your Company'}
                  </h4>
                  {tagline && showTaglineInSidebar && (
                    <p 
                      className="text-xs"
                      style={{ 
                        fontFamily: taglineFont,
                        color: taglineColor 
                      }}
                    >
                      {tagline}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Browser Tab Preview */}
            <div className="p-4 bg-gray-200">
              <p className="text-xs text-gray-500 mb-2">Browser tab will show:</p>
              {/* Browser chrome bar */}
              <div className="bg-gray-300 rounded-t-md pt-2 px-2">
                {/* The tab itself */}
                <div className="inline-flex items-center gap-1.5 bg-white rounded-t-md px-3 py-1.5 max-w-xs shadow-sm border border-b-0 border-gray-300">
                  {/* Favicon */}
                  <div className="w-3.5 h-3.5 rounded-sm bg-blue-500 flex-shrink-0 flex items-center justify-center">
                    <div className="w-1.5 h-1.5 bg-white rounded-full" />
                  </div>
                  {/* Tab title */}
                  <span className="text-xs font-medium text-gray-700 truncate max-w-[180px]">
                    {(tagline && showTaglineInBrowserTitle)
                      ? `${companyName || 'Your Company'} - ${tagline}`
                      : companyName || 'Your Company'}
                  </span>
                  {/* Close button */}
                  <div className="ml-1 w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 text-gray-400 hover:bg-gray-200">
                    <svg className="w-2.5 h-2.5" viewBox="0 0 8 8" fill="none">
                      <path d="M1 1l6 6M7 1L1 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                  </div>
                </div>
              </div>
              {/* Address bar strip */}
              <div className="bg-gray-300 rounded-b-md pb-2 px-2">
                <div className="bg-gray-100 rounded px-2 py-1 flex items-center gap-1">
                  <div className="w-2.5 h-2.5 rounded-full border border-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-400 truncate">treegenie.ai</span>
                </div>
              </div>
            </div>

            {/* Team URL Preview */}
            {teamSlug && (
              <div className="p-4 bg-gray-50 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2">Team login URL:</p>
                <code className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  {window.location.origin}/team/{teamSlug}
                </code>
              </div>
            )}
          </div>

          {/* Preview Info */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              💡 <strong>Your branding appears on:</strong>
            </p>
            <ul className="text-sm text-blue-600 mt-2 ml-4 list-disc">
              <li>App sidebar header</li>
              <li>Browser tab title</li>
              <li>Team login page (if URL set)</li>
            </ul>
          </div>
        </Card>
      </div>
      {/* Reset Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={async () => {
          await handleResetToDefaults();
          setShowResetConfirm(false);
        }}
        title="Reset Branding?"
        message="Reset all branding to defaults? Your custom logo, colors, and company name will be removed."
        confirmLabel="Reset to Defaults"
        confirmVariant="danger"
        isLoading={isSaving}
      />
    </div>
  );
};

export default TeamBrandingSettings;

